// frontend/src/pages/AdminPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, MessageSquare, Film, BookOpen, Trash2,
  Loader, Search, ShieldAlert, BarChart2, Star
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import api from '../services/api';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface Stats {
  totalUsers: number;
  totalReviews: number;
  totalMovies: number;
  totalNotebookEntries: number;
  recentUsers: { id: string; username: string; name: string; avatar?: string; createdAt: string }[];
}

interface AdminUser {
  id: string;
  username: string;
  name: string;
  email: string;
  avatar?: string;
  isAdmin: boolean;
  isVerified: boolean;
  createdAt: string;
  _count: { reviews: number; favorites: number; followers: number };
}

interface AdminReview {
  id: string;
  content: string;
  rating: number;
  createdAt: string;
  likesCount: number;
  commentsCount: number;
  user: { id: string; username: string; avatar?: string };
  movie: { id: number; title: string; posterUrl?: string };
}

interface AdminMovie {
  id: number;
  title: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  reviewsCount: number;
  favoritesCount: number;
}

type Tab = 'dashboard' | 'users' | 'reviews' | 'movies';

// ─────────────────────────────────────────────
// CONFIRM DIALOG
// ─────────────────────────────────────────────
const ConfirmDialog: React.FC<{
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-[#faf8f6] rounded-2xl p-6 max-w-sm w-full shadow-xl"
    >
      <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
      <p className="text-center text-gray-700 font-medium mb-6">{message}</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium">
          Cancel
        </button>
        <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 text-sm font-medium">
          Delete
        </button>
      </div>
    </motion.div>
  </div>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();

  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [movies, setMovies] = useState<AdminMovie[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Guard — redirect if not admin
  useEffect(() => {
    if (!state.isLoading && state.currentUser && !state.currentUser.isAdmin) {
      navigate('/');
    }
    if (!state.isLoading && !state.currentUser) {
      navigate('/login');
    }
  }, [state.currentUser, state.isLoading, navigate]);

  // ── Fetch stats ──
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Fetch users ──
  const fetchUsers = useCallback(async (q = '') => {
    setIsLoading(true);
    try {
      const res = await api.get(`/admin/users?limit=50${q ? `&q=${q}` : ''}`);
      setUsers(res.data.data.users);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Fetch reviews ──
  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/admin/reviews?limit=50');
      setReviews(res.data.data.reviews);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Fetch movies ──
  const fetchMovies = useCallback(async (q = '') => {
    setIsLoading(true);
    try {
      const res = await api.get(`/admin/movies?limit=50${q ? `&q=${q}` : ''}`);
      setMovies(res.data.data.movies);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchStats();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'reviews') fetchReviews();
    if (activeTab === 'movies') fetchMovies();
    setSearch('');
  }, [activeTab, fetchStats, fetchUsers, fetchReviews, fetchMovies]);

  // ── Search debounce ──
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'users') fetchUsers(search);
      if (activeTab === 'movies') fetchMovies(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, activeTab, fetchUsers, fetchMovies]);

  // ── Delete handlers ──
  const handleDeleteUser = (user: AdminUser) => {
    setConfirm({
      message: `Delete @${user.username}? This will remove all their data permanently.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/admin/users/${user.id}`);
          setUsers(prev => prev.filter(u => u.id !== user.id));
          showToast(`@${user.username} deleted`);
        } catch { showToast('Failed to delete user'); }
      },
    });
  };

  const handleDeleteReview = (review: AdminReview) => {
    setConfirm({
      message: `Delete this review by @${review.user.username}?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/admin/reviews/${review.id}`);
          setReviews(prev => prev.filter(r => r.id !== review.id));
          showToast('Review deleted');
        } catch { showToast('Failed to delete review'); }
      },
    });
  };

  const handleDeleteMovie = (movie: AdminMovie) => {
    setConfirm({
      message: `Delete "${movie.title}"? This will also remove all reviews and favorites for this movie.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/admin/movies/${movie.id}`);
          setMovies(prev => prev.filter(m => m.id !== movie.id));
          showToast(`"${movie.title}" deleted`);
        } catch { showToast('Failed to delete movie'); }
      },
    });
  };

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart2 },
    { key: 'users', label: 'Users', icon: Users },
    { key: 'reviews', label: 'Reviews', icon: MessageSquare },
    { key: 'movies', label: 'Movies', icon: Film },
  ];

  if (!state.currentUser?.isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-xl text-sm shadow-lg"
        >
          {toast}
        </motion.div>
      )}

      {/* Confirm dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className="container mx-auto px-4 pt-24 pb-16 max-w-6xl">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <ShieldAlert className="w-8 h-8 text-romcom-red" />
          <div>
            <h1 className="text-3xl font-playfair font-bold text-gray-800">Admin Panel</h1>
            <p className="text-gray-500 text-sm">Manage RomComTHIS</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                activeTab === tab.key
                  ? 'bg-romcom-red text-white shadow-md'
                  : 'bg-[#faf8f6] text-gray-600 border border-romcom-pink/20 hover:bg-[#f0f0f0]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader className="w-8 h-8 text-romcom-red animate-spin" />
          </div>
        ) : (
          <>
            {/* ── DASHBOARD ── */}
            {activeTab === 'dashboard' && stats && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { icon: Users, label: 'Total Users', value: stats.totalUsers, color: 'text-blue-500', bg: 'bg-blue-50' },
                    { icon: MessageSquare, label: 'Total Reviews', value: stats.totalReviews, color: 'text-yellow-500', bg: 'bg-yellow-50' },
                    { icon: Film, label: 'Total Movies', value: stats.totalMovies, color: 'text-purple-500', bg: 'bg-purple-50' },
                    { icon: BookOpen, label: 'Notebook Entries', value: stats.totalNotebookEntries, color: 'text-pink-500', bg: 'bg-pink-50' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-[#faf8f6] rounded-2xl p-5 shadow-sm border border-romcom-pink/20 text-center">
                      <div className={`${stat.bg} w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2`}>
                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                      <div className="text-3xl font-bold text-gray-800">{stat.value}</div>
                      <div className="text-gray-500 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-[#faf8f6] rounded-2xl p-5 shadow-sm border border-romcom-pink/20 text-center">
                  <h3 className="font-bold text-gray-800 mb-4">Recently Joined</h3>
                  <div className="space-y-3">
                    {stats.recentUsers.map(user => (
                      <div key={user.id} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-sm font-bold">
                          {user.avatar
                            ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                            : user.username.charAt(0).toUpperCase()
                          }
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{user.name}</p>
                          <p className="text-xs text-gray-400">@{user.username}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── USERS ── */}
            {activeTab === 'users' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-romcom-red text-sm bg-[#faf8f6]"
                  />
                </div>
                <div className="bg-[#faf8f6] rounded-2xl shadow-sm border border-romcom-pink/20 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">User</th>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium hidden md:table-cell">Email</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium">Reviews</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium hidden md:table-cell">Followers</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium">Verified</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-xs font-bold">
                                {user.avatar
                                  ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                                  : user.username.charAt(0).toUpperCase()
                                }
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{user.name}</p>
                                <p className="text-xs text-gray-400">@{user.username} {user.isAdmin && <span className="text-romcom-red">· admin</span>}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-gray-500 hidden md:table-cell">{user.email}</td>
                          <td className="px-3 py-3 text-center text-gray-600">{user._count.reviews}</td>
                          <td className="px-3 py-3 text-center text-gray-600 hidden md:table-cell">{user._count.followers}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.isVerified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {user.isVerified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right">
                            {!user.isAdmin && (
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-12 text-gray-400">No users found</div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── REVIEWS ── */}
            {activeTab === 'reviews' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                {reviews.map(review => (
                  <div key={review.id} className="bg-[#faf8f6] rounded-2xl p-4 shadow-sm border border-romcom-pink/20 flex gap-4">
                    <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {review.movie.posterUrl
                        ? <img src={review.movie.posterUrl} alt="" className="w-full h-full object-cover" />
                        : <Film className="w-6 h-6 text-gray-300 m-auto mt-4" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{review.movie.title}</p>
                          <p className="text-xs text-gray-400">by @{review.user.username} · {new Date(review.createdAt).toLocaleDateString()}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteReview(review)}
                          className="p-1.5 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1 my-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm line-clamp-2">{review.content}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-400">
                        <span>❤️ {review.likesCount}</span>
                        <span>💬 {review.commentsCount}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <div className="text-center py-12 text-gray-400 bg-[#faf8f6] rounded-2xl border border-romcom-pink/20">No reviews found</div>
                )}
              </motion.div>
            )}

            {/* ── MOVIES ── */}
            {activeTab === 'movies' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search movies..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-romcom-red text-sm bg-[#faf8f6]"
                  />
                </div>
                 <div className="bg-[#faf8f6] rounded-2xl p-4 shadow-sm border border-romcom-pink/20 flex gap-4">

                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-5 py-3 text-gray-500 font-medium">Movie</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium hidden md:table-cell">Year</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium">Reviews</th>
                        <th className="text-center px-3 py-3 text-gray-500 font-medium hidden md:table-cell">Favorites</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {movies.map(movie => (
                        <tr key={movie.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100">
                                {movie.posterUrl
                                  ? <img src={movie.posterUrl} alt="" className="w-full h-full object-cover" />
                                  : <Film className="w-4 h-4 text-gray-300 m-auto mt-4" />
                                }
                              </div>
                              <p className="font-medium text-gray-800">{movie.title}</p>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center text-gray-500 hidden md:table-cell">{movie.releaseYear || '—'}</td>
                          <td className="px-3 py-3 text-center text-gray-600">{movie.reviewsCount}</td>
                          <td className="px-3 py-3 text-center text-gray-600 hidden md:table-cell">{movie.favoritesCount}</td>
                          <td className="px-3 py-3 text-right">
                            <button
                              onClick={() => handleDeleteMovie(movie)}
                              className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                              title="Delete movie"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {movies.length === 0 && (
                    <div className="text-center py-12 text-gray-400">No movies found</div>
                  )}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;